import { useRef, useState } from 'react';
import { store } from '@/ui/store';
import { useStore } from '@/ui/useStore';
import { EXAMPLES } from '@/examples';
import { deserialize, serialize } from '@/model/persistFormat';
import { downloadText, toCsv } from '@/grid/clipboard';
import { cellRawText, getCell } from '@/model/workbook';
import { formatValue, getCurrency } from '@/engine/format';
import { Popover, rectOf } from '@/ui/Popover';
import { looksLikeZip, type Bytes } from '@/data/zip';
import { importCaveats, importXlsx, type XlsxImport } from '@/data/xlsxImport';
import { ImportChoiceDialog } from '@/ui/ImportChoiceDialog';
import { looksLikeLegacyXls } from '@/data/xlsx';
import { exportXlsx, type ExportName, type ExportSource } from '@/data/xlsxExport';
import { buildLinkedJson, exportLinkedXlsx } from '@/data/linkedExport';
import { projectKey } from '@/giraffe/persist';
import { linkController } from '@/giraffe/linkController';

/**
 * File: everything that moves a whole document in or out — new, import, save a
 * copy, export a sheet, and the examples.
 *
 * One menu rather than four toolbar buttons, because these are the things a
 * user reaches for once a session, and the toolbar's room belongs to the things
 * they reach for once a minute. The project SAVE is deliberately not in here:
 * it is a state as much as an action ("Saved ✓", "Save •"), and a state nobody
 * can see is not one.
 *
 * Anything that replaces the workbook asks first, when there is work to lose.
 */
export function FileMenu(): JSX.Element {
  useStore();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  // Set only when an .xlsx import needs the Add/Replace/Cancel choice — see
  // ImportChoiceDialog. Cleared once the user picks one.
  const [importChoice, setImportChoice] = useState<{ imported: XlsxImport; fileName: string } | null>(null);

  // System sheets do not count: the Usages sheet is always populated, so
  // including it would put "this replaces the workbook that is open now" in
  // front of someone whose workbook is empty.
  const hasWork = store.wb.sheets.some((s) => s.role === undefined && Object.keys(s.cells).length > 0);

  /*
   * Say what it actually costs. Opening an example replaces the workbook on
   * screen AND, at the next save, the copy held in the project — which is the
   * half nobody expects and the half that cannot be taken back.
   */
  const confirmReplace = (what: string): boolean =>
    !hasWork ||
    confirm(
      `Open ${what}?\n\nThis replaces the workbook open now, and the copy saved in this project once it next saves. Use File ▸ Save a .json copy first if you want to keep it.`
    );

  /**
   * Two formats, one picker, and the FILE says which is which.
   *
   * Sniffed rather than switched on the extension: `.xlsx` is a zip and starts
   * `PK`, a saved workbook is JSON, and a file renamed by hand is still whatever
   * it actually is. Being wrong here means an unreadable-file alert over a file
   * that reads perfectly well.
   */
  const openFile = async (file: File): Promise<void> => {
    const data: Bytes = new Uint8Array(await file.arrayBuffer());
    // .xlsx gets its OWN replace-vs-add choice below, once we know what's in
    // it — the generic "this replaces" gate only applies to a .json workbook.
    if (looksLikeZip(data)) return openXlsx(data, file.name);
    if (looksLikeLegacyXls(data)) {
      alert(
        `${file.name} is the older .xls format, which gTables cannot read.\n\nOpen it in Excel or Google Sheets and save it as .xlsx, then import that. Copying the cells and pasting them in also works.`
      );
      return;
    }
    if (!confirmReplace(file.name)) return;

    try {
      const wb = deserialize(JSON.parse(new TextDecoder().decode(data)));
      if (!wb) {
        alert('That file is not a gTables workbook, or it was saved by a newer version.');
        return;
      }
      store.loadWorkbook(wb);
    } catch {
      alert('That file could not be read — a workbook here is either a .xlsx or a .json saved from this menu.');
    }
  };

  const finishImport = (imported: XlsxImport, mode: 'add' | 'replace'): void => {
    const demoted = mode === 'add' ? store.addImportedSheets(imported) : store.loadImported(imported);
    const caveats = importCaveats(imported.report, demoted);
    // Silence is the success case: the tabs at the bottom of the window are a
    // better "it worked" than a dialog to dismiss.
    if (caveats) alert(caveats);
  };

  const openXlsx = async (data: Bytes, fileName: string): Promise<void> => {
    try {
      const imported = await importXlsx(data, fileName);
      // Only an xlsx offers a middle ground: there IS something to lose, but
      // not necessarily something to replace — see ImportChoiceDialog.
      if (hasWork) {
        setImportChoice({ imported, fileName });
        return;
      }
      finishImport(imported, 'replace');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'That Excel file could not be read.');
    }
  };

  /**
   * The active sheet as CSV, as it READS — a date column exported as 46025
   * would be useless to whatever opens the file.
   */
  const exportCsv = (): void => {
    const sheet = store.sheet();
    let r2 = 1;
    let c2 = 1;
    for (const key of Object.keys(sheet.cells)) {
      const comma = key.indexOf(',');
      if (cellRawText(sheet.cells[key]) === '') continue;
      r2 = Math.max(r2, Number(key.slice(0, comma)));
      c2 = Math.max(c2, Number(key.slice(comma + 1)));
    }
    const rows: string[][] = [];
    for (let r = 1; r <= r2; r++) {
      const line: string[] = [];
      for (let c = 1; c <= c2; c++) {
        line.push(formatValue(store.engine.valueAt(sheet, r, c) ?? null, getCell(sheet, r, c)?.fmt));
      }
      rows.push(line);
    }
    downloadText(`${sheet.name}.csv`, toCsv(rows));
  };

  /**
   * Every sheet as a static .xlsx — a frozen snapshot, nothing in it still
   * talking to this project. Formulas stay formulas; a GI.* one goes as the
   * value it shows, because Excel has no project to ask — see
   * data/xlsxExport.
   *
   * Analytics is left out when it is hidden (Dev Mode off) — see
   * SheetTabs.tsx's same `role !== 'analytics'` filter. A tab nobody outside
   * Dev Mode can even see on screen has no business in a file they export,
   * either.
   */
  const exportWorkbook = async (): Promise<void> => {
    const blockNames = new Map(Object.values(store.wb.blocks).map((b) => [b.name.toLowerCase(), b.name]));
    const names: ExportName[] = [...store.engine.namedRanges()].map(([key, r]) => ({
      name: blockNames.get(key) ?? key,
      ...r,
    }));
    const sheets = store.devMode ? store.wb.sheets : store.wb.sheets.filter((s) => s.role !== 'analytics');
    const wb = sheets === store.wb.sheets ? store.wb : { ...store.wb, sheets };
    try {
      const { bytes } = await exportXlsx(wb, {
        valueAt: (sheet, row, col) => store.engine.valueAt(sheet, row, col),
        names,
        currency: getCurrency(),
      });
      downloadText('workbook.xlsx', bytes, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'The workbook could not be exported.');
    }
  };

  /**
   * Areas, plus every custom sheet, plus a `GI.*` LAMBDA set over them — see
   * data/giLambdas.ts and data/linkedExport.ts for exactly what this does and
   * does not include yet (no Power Query auto-refresh, no Excel Tables,
   * nothing verified against real Excel). Features, Blocks, Usages and
   * Analytics are deliberately left out — Areas alone already carries
   * everything a GI.* formula needs. Downloads a second file alongside the
   * workbook: the same tables as JSON, for a Power Query setup to read once
   * someone finishes that by hand.
   */
  const exportLinked = async (): Promise<void> => {
    const source: ExportSource = {
      valueAt: (sheet, row, col) => store.engine.valueAt(sheet, row, col),
      currency: getCurrency(),
    };
    try {
      const { bytes } = await exportLinkedXlsx(store.wb, source);
      const id = projectKey();
      const xlsxName = `Giraffe_${id}.xlsx`;
      const jsonName = `Giraffe_${id}.json`;
      downloadText(xlsxName, bytes, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      const json = buildLinkedJson(store.wb, source, new Date().toISOString());
      const jsonText = JSON.stringify(json, null, 2);
      downloadText(jsonName, jsonText, 'application/json');
      // Refresh the on-save diff's baseline to what was just exported, and
      // push this copy through now if already linked — otherwise a linked
      // file would keep comparing against a stale export.
      void linkController.save(jsonName, jsonText, bytes.buffer);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'The linked workbook could not be exported.');
    }
  };

  return (
    <div className="gt-menu">
      <button
        ref={buttonRef}
        className={`gt-btn${open ? ' is-on' : ''}`}
        onClick={() => setOpen((o) => !o)}
        title="New, import, save a copy, export, examples"
      >
        File ▾
      </button>
      {open ? (
        <Popover
          anchor={rectOf(buttonRef.current)}
          align="start"
          onClose={() => setOpen(false)}
          className="gt-menu-list"
        >
          <button
            className="gt-menu-item"
            title="Starts over. The project's usages come back on their own."
            onClick={() => {
              setOpen(false);
              if (!confirmReplace('a blank workbook')) return;
              store.loadWorkbook(store.newWorkbook());
            }}
          >
              <span className="gt-menu-name">New blank workbook</span>
          </button>
          <button
            className="gt-menu-item"
            title="An .xlsx from Excel — every sheet in it — or a .json saved here."
            onClick={() => {
              setOpen(false);
              fileRef.current?.click();
            }}
          >
              <span className="gt-menu-name">Import a workbook…</span>
          </button>

          <div className="gt-menu-sep" />

          <button
            className="gt-menu-item"
            title="Formulas and live blocks — the values recompute."
            onClick={() => {
              setOpen(false);
              downloadText('workbook.json', JSON.stringify(serialize(store.wb), null, 2), 'application/json');
            }}
          >
              <span className="gt-menu-name">Save a .json copy</span>
          </button>
          <button
            className="gt-menu-item"
            title="The values as they read, for anything that is not this app."
            onClick={() => {
              setOpen(false);
              exportCsv();
            }}
          >
              <span className="gt-menu-name">Export this sheet as CSV</span>
          </button>
          <button
            className="gt-menu-item"
            title="Every sheet, for Excel. Formulas stay live; GI.* cells go as the value they show now. Analytics is left out while it is hidden."
            onClick={() => {
              setOpen(false);
              void exportWorkbook();
            }}
          >
              <span className="gt-menu-name">Export workbook as static .xlsx</span>
          </button>
          {store.devMode ? (
            <button
              className="gt-menu-item"
              title="Areas, plus your own custom sheets, plus GI.* formulas that work directly in Excel — and a companion .json file for a future live-refresh setup. Unverified against real Excel; open it there and check before relying on it."
              onClick={() => {
                setOpen(false);
                void exportLinked();
              }}
            >
                <span className="gt-menu-name">Export workbook as linked .xlsx</span>
            </button>
          ) : null}

          <div className="gt-menu-sep" />
          <div className="gt-menu-heading">
            Examples <span className="gt-menu-caveat">· illustrative only</span>
          </div>

          {EXAMPLES.map((example) => (
            <button
              key={example.id}
              className="gt-menu-item"
              title={example.summary}
              onClick={() => {
                setOpen(false);
                if (!confirmReplace(example.name)) return;
                store.loadWorkbook(example.build());
              }}
            >
              <span className="gt-menu-name">{example.name}</span>
            </button>
          ))}

          <div className="gt-menu-sep" />
          <button
            className="gt-menu-item"
            title="Shows the features being superseded by live Giraffe analytics — the Database wizard. Off by default; persists in this browser."
            onClick={() => store.toggleDevMode()}
          >
            <span className="gt-menu-name">{store.devMode ? '✓ ' : ''}Dev Mode</span>
          </button>
          <button
            className="gt-menu-item"
            title={mcpTitle()}
            onClick={() => store.toggleMcp()}
          >
            <span className="gt-menu-name">{store.mcpEnabled ? '✓ ' : ''}MCP{mcpBadge()}</span>
          </button>
        </Popover>
      ) : null}
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void openFile(file);
        }}
      />
      {importChoice ? (
        <ImportChoiceDialog
          fileName={importChoice.fileName}
          sheetCount={importChoice.imported.workbook.sheets.length}
          onAdd={() => {
            finishImport(importChoice.imported, 'add');
            setImportChoice(null);
          }}
          onReplace={() => {
            finishImport(importChoice.imported, 'replace');
            setImportChoice(null);
          }}
          onCancel={() => setImportChoice(null)}
        />
      ) : null}
    </div>
  );
}

/** What the MCP item says about the relay, beside its name. */
function mcpBadge(): string {
  const s = store.mcpStatus;
  if (!store.mcpEnabled || !s) return '';
  if (s.state === 'connected') return ' · connected';
  if (s.state === 'connecting') return ' · connecting…';
  if (s.state === 'dropped') return ' · reconnecting…';
  return ' · off';
}

function mcpTitle(): string {
  const s = store.mcpStatus;
  const base =
    'Lets an AI agent with the Giraffe MCP connector read this workbook, evaluate formulas, and write to ordinary sheets (gtables__* tools). Persists in this browser.';
  if (s?.state === 'stopped' || s?.state === 'unavailable') return `${base}\n\n${s.reason}`;
  return base;
}
